import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Heart,
  Calendar,
  Briefcase,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Users,
} from "lucide-react";

export type EmploymentChangeType = 
  | "employmentType" 
  | "scheduledHours" 
  | "hireDate";

export interface EmploymentChangeData {
  type: EmploymentChangeType;
  field: string;
  oldValue: string | number | null;
  newValue: string | number | null;
  employeeName?: string;
  currentEnrollments?: any[];
  waitingPeriodDays?: number;
}

export interface VerificationResult {
  confirmed: boolean;
  effectiveDate: string;
  reason: string;
  notes?: string;
}

interface ImpactItem {
  type: "gain" | "loss" | "warning" | "info";
  title: string;
  description: string;
  icon: React.ReactNode;
}

const EMPLOYMENT_CHANGE_REASONS = [
  { value: "promotion", label: "Promotion" },
  { value: "demotion", label: "Demotion" },
  { value: "role_change", label: "Role/Position Change" },
  { value: "schedule_adjustment", label: "Schedule Adjustment" },
  { value: "business_needs", label: "Business Needs" },
  { value: "employee_request", label: "Employee Request" },
  { value: "performance_based", label: "Performance-Based" },
  { value: "organizational_restructure", label: "Organizational Restructure" },
  { value: "seasonal_adjustment", label: "Seasonal Adjustment" },
  { value: "aca_compliance", label: "ACA Compliance Adjustment" },
  { value: "variable_hour_reclassification", label: "Variable Hour Reclassification" },
  { value: "reduction_in_force", label: "Reduction in Force (RIF)" },
  { value: "temporary_layoff", label: "Temporary Layoff" },
  { value: "fmla_leave_return", label: "FMLA/Leave Return" },
  { value: "military_leave_return", label: "Military Leave Return" },
  { value: "workers_comp_return", label: "Workers' Comp Return to Work" },
  { value: "disability_accommodation", label: "Disability Accommodation" },
  { value: "merger_acquisition", label: "Merger/Acquisition Transfer" },
  { value: "union_agreement", label: "Union/CBA Agreement" },
  { value: "correction", label: "Data Correction" },
  { value: "other", label: "Other" },
];

const HIRE_DATE_CHANGE_REASONS = [
  { value: "data_correction", label: "Data Correction" },
  { value: "rehire", label: "Rehire (New Employment)" },
  { value: "return_from_leave", label: "Return from Leave (No Break in Service)" },
  { value: "acquisition_transfer", label: "Company Acquisition/Transfer" },
  { value: "temp_to_perm", label: "Temporary to Permanent Conversion" },
  { value: "seasonal_return", label: "Seasonal Employee Return" },
  { value: "break_in_service_reset", label: "Break in Service - Eligibility Reset" },
  { value: "eligibility_reset", label: "Eligibility Period Reset" },
  { value: "tenure_adjustment", label: "Tenure/Seniority Adjustment" },
  { value: "administrative_error", label: "Administrative Error Correction" },
  { value: "other", label: "Other" },
];

const ACA_FULL_TIME_THRESHOLD = 30;

function isFullTimeEmploymentType(type: string | null | undefined): boolean {
  if (!type) return false;
  const normalized = type.toLowerCase().trim();
  return normalized === "full-time" || normalized === "full time" || normalized === "fulltime";
}

function isFullTimeByHours(hours: number | null | undefined): boolean {
  if (hours === null || hours === undefined) return false;
  return hours >= ACA_FULL_TIME_THRESHOLD;
}

function isVariableHourType(type: string | null | undefined): boolean {
  if (!type) return false;
  const normalized = type.toLowerCase().trim();
  return normalized.includes("variable") || normalized.includes("seasonal") || normalized.includes("temporary");
}

function analyzeEmploymentTypeChange(
  oldType: string | null,
  newType: string | null,
  currentEnrollments: any[] = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  waitingPeriodDays: number = 0,
): ImpactItem[] {
  const impacts: ImpactItem[] = [];
  const wasFullTime = isFullTimeEmploymentType(oldType);
  const willBeFullTime = isFullTimeEmploymentType(newType);
  const hasEnrollments = currentEnrollments.length > 0;
  const wasVariableHour = isVariableHourType(oldType);
  const willBeVariableHour = isVariableHourType(newType);

  if (!wasFullTime && willBeFullTime) {
    impacts.push({
      type: "gain",
      title: "Benefits Eligibility Gained",
      description: "Employee will become eligible for employer-sponsored health benefits.",
      icon: <Heart className="h-4 w-4 text-green-600" />,
    });
    
    impacts.push({
      type: "info",
      title: "Waiting Period Applies",
      description: "A waiting period may apply before benefits become effective. Check your plan documents for specific waiting period requirements.",
      icon: <Clock className="h-4 w-4 text-blue-600" />,
    });

    impacts.push({
      type: "info",
      title: "Time-Off Policy Changes",
      description: "Employee may become eligible for additional time-off benefits such as PTO, vacation, and sick leave accrual based on employment class.",
      icon: <Calendar className="h-4 w-4 text-blue-600" />,
    });

    impacts.push({
      type: "info",
      title: "ACA Status Update",
      description: "Employee will be classified as a Full-Time Employee (FTE) for ACA reporting purposes. If previously Variable Hour, this ends their measurement period.",
      icon: <Briefcase className="h-4 w-4 text-blue-600" />,
    });

    if (wasVariableHour) {
      impacts.push({
        type: "info",
        title: "Variable Hour Reclassification",
        description: "Employee is being reclassified from Variable Hour to Full-Time. Previous measurement period calculations no longer apply.",
        icon: <TrendingUp className="h-4 w-4 text-blue-600" />,
      });
    }
  }

  if (wasFullTime && !willBeFullTime) {
    if (hasEnrollments) {
      impacts.push({
        type: "loss",
        title: "Benefits Eligibility Lost",
        description: `Employee has ${currentEnrollments.length} active enrollment(s) that will be terminated. Coverage will end on the effective date.`,
        icon: <XCircle className="h-4 w-4 text-red-600" />,
      });

      impacts.push({
        type: "warning",
        title: "Dependent Coverage Affected",
        description: "Enrolled dependents will also lose coverage. Review each dependent's coverage status.",
        icon: <Users className="h-4 w-4 text-amber-600" />,
      });
    } else {
      impacts.push({
        type: "warning",
        title: "Benefits Eligibility Ends",
        description: "Employee will no longer be eligible for employer-sponsored health benefits.",
        icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
      });
    }

    impacts.push({
      type: "warning",
      title: "COBRA Notification Required",
      description: "If employee was enrolled in health benefits, you must provide COBRA continuation coverage notice within 14 days of the qualifying event.",
      icon: <AlertCircle className="h-4 w-4 text-amber-600" />,
    });

    impacts.push({
      type: "warning",
      title: "Time-Off Policy Changes",
      description: "Employee's time-off benefits and accrual rates may be reduced or eliminated. Review company policy for the new employment type.",
      icon: <Calendar className="h-4 w-4 text-amber-600" />,
    });

    impacts.push({
      type: "info",
      title: "ACA Stability Period Consideration",
      description: "If employee is in an ACA stability period, they may retain coverage until the stability period ends, regardless of hours reduction. Review your ACA policy.",
      icon: <Briefcase className="h-4 w-4 text-blue-600" />,
    });

    if (willBeVariableHour) {
      impacts.push({
        type: "info",
        title: "Variable Hour Classification",
        description: "Employee is now classified as Variable Hour. A new measurement period will be used to determine future full-time status for ACA purposes.",
        icon: <TrendingDown className="h-4 w-4 text-blue-600" />,
      });
    }
  }

  // Use case-insensitive comparison to avoid false positives like "Full-time" vs "full-time"
  const normalizedOld = oldType?.toLowerCase?.()?.trim?.() || null;
  const normalizedNew = newType?.toLowerCase?.()?.trim?.() || null;
  if (wasFullTime === willBeFullTime && normalizedOld !== normalizedNew) {
    impacts.push({
      type: "info",
      title: "Employment Classification Change",
      description: `Employee status changing from ${oldType || "Unknown"} to ${newType}. Benefits eligibility status remains unchanged.`,
      icon: <Briefcase className="h-4 w-4 text-blue-600" />,
    });

    if ((newType?.toLowerCase().includes("contract") || newType?.toLowerCase().includes("temporary")) && 
        !(oldType?.toLowerCase().includes("contract") || oldType?.toLowerCase().includes("temporary"))) {
      impacts.push({
        type: "warning",
        title: "Contractor/Temporary Status",
        description: "Changing to contractor or temporary status may affect benefits eligibility based on your company's policies, even if hours remain the same.",
        icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
      });
    }
  }

  return impacts;
}

function analyzeScheduledHoursChange(
  oldHours: number | null,
  newHours: number | null,
  currentEnrollments: any[] = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  waitingPeriodDays: number = 0,
): ImpactItem[] {
  const impacts: ImpactItem[] = [];
  const wasFullTime = isFullTimeByHours(oldHours);
  const willBeFullTime = isFullTimeByHours(newHours);
  const hasEnrollments = currentEnrollments.length > 0;

  if (!wasFullTime && willBeFullTime) {
    impacts.push({
      type: "gain",
      title: "ACA Full-Time Threshold Met",
      description: `Scheduled hours of ${newHours}/week meets the ACA threshold of 30+ hours. Employee becomes eligible for benefits.`,
      icon: <TrendingUp className="h-4 w-4 text-green-600" />,
    });

    impacts.push({
      type: "gain",
      title: "Benefits Eligibility Gained",
      description: "Employee will become eligible for employer-sponsored health benefits.",
      icon: <Heart className="h-4 w-4 text-green-600" />,
    });

    impacts.push({
      type: "info",
      title: "Waiting Period Applies",
      description: "A waiting period may apply before benefits become effective. Check your plan documents for specific waiting period requirements.",
      icon: <Clock className="h-4 w-4 text-blue-600" />,
    });

    impacts.push({
      type: "info",
      title: "ACA Measurement Period",
      description: "For Variable Hour employees, this scheduled hours change may require review of the current measurement period status and stability period eligibility.",
      icon: <Briefcase className="h-4 w-4 text-blue-600" />,
    });
  }

  if (wasFullTime && !willBeFullTime) {
    impacts.push({
      type: "loss",
      title: "ACA Full-Time Threshold No Longer Met",
      description: `Scheduled hours of ${newHours}/week is below the ACA threshold of 30 hours. Employee loses full-time status.`,
      icon: <TrendingDown className="h-4 w-4 text-red-600" />,
    });

    if (hasEnrollments) {
      impacts.push({
        type: "loss",
        title: "Benefits Eligibility Lost",
        description: `Employee has ${currentEnrollments.length} active enrollment(s) that may be terminated.`,
        icon: <XCircle className="h-4 w-4 text-red-600" />,
      });

      impacts.push({
        type: "warning",
        title: "Dependent Coverage Affected",
        description: "Enrolled dependents will also lose coverage when employee coverage terminates.",
        icon: <Users className="h-4 w-4 text-amber-600" />,
      });
    } else {
      impacts.push({
        type: "warning",
        title: "Benefits Eligibility Ends",
        description: "Employee will no longer be eligible for employer-sponsored health benefits.",
        icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
      });
    }

    impacts.push({
      type: "warning",
      title: "COBRA Notification Required",
      description: "If hours reduction causes loss of coverage, COBRA notification must be provided within 14 days.",
      icon: <AlertCircle className="h-4 w-4 text-amber-600" />,
    });

    impacts.push({
      type: "info",
      title: "ACA Stability Period Check",
      description: "If employee is in an ACA stability period, they may retain coverage until the period ends. Review your Look-Back Measurement Method settings.",
      icon: <Briefcase className="h-4 w-4 text-blue-600" />,
    });
  }

  if (wasFullTime === willBeFullTime && oldHours !== newHours) {
    const direction = (newHours || 0) > (oldHours || 0) ? "increased" : "decreased";
    impacts.push({
      type: "info",
      title: "Hours Adjustment",
      description: `Weekly scheduled hours ${direction} from ${oldHours || 0} to ${newHours}. ACA full-time status remains unchanged.`,
      icon: <Clock className="h-4 w-4 text-blue-600" />,
    });

    if (wasFullTime && (newHours || 0) < 32) {
      impacts.push({
        type: "warning",
        title: "Approaching Threshold",
        description: "New scheduled hours are close to the 30-hour ACA threshold. Monitor actual hours to maintain compliance.",
        icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
      });
    }
  }

  return impacts;
}

function analyzeHireDateChange(
  oldDate: string | null,
  newDate: string | null,
  currentEnrollments: any[] = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  waitingPeriodDays: number = 0,
): ImpactItem[] {
  const impacts: ImpactItem[] = [];

  if (!oldDate && newDate) {
    impacts.push({
      type: "info",
      title: "Hire Date Set",
      description: `Hire date being set to ${formatDate(newDate)}. This will be used to calculate eligibility dates and tenure.`,
      icon: <Calendar className="h-4 w-4 text-blue-600" />,
    });

    impacts.push({
      type: "info",
      title: "Benefits Waiting Period Starts",
      description: `Benefits eligibility waiting period begins from ${formatDate(newDate)}. Check your plan documents for specific waiting period requirements.`,
      icon: <Clock className="h-4 w-4 text-blue-600" />,
    });
  } else if (oldDate && newDate && oldDate !== newDate) {
    const oldDateObj = new Date(oldDate);
    const newDateObj = new Date(newDate);
    const isEarlier = newDateObj < oldDateObj;
    const daysDiff = Math.abs(Math.round((newDateObj.getTime() - oldDateObj.getTime()) / (1000 * 60 * 60 * 24)));
    const isSignificantChange = daysDiff >= 13; // IRS break in service threshold is 13+ weeks

    impacts.push({
      type: "warning",
      title: "Hire Date Modification",
      description: `Hire date changing from ${formatDate(oldDate)} to ${formatDate(newDate)} (${daysDiff} day${daysDiff !== 1 ? "s" : ""} ${isEarlier ? "earlier" : "later"}).`,
      icon: <Calendar className="h-4 w-4 text-amber-600" />,
    });

    impacts.push({
      type: "warning",
      title: "Benefits Eligibility Date Affected",
      description: isEarlier
        ? `Employee's benefits eligibility date may move earlier. Review waiting period calculations.`
        : `Employee's benefits eligibility date may be delayed. This could affect current enrollments.`,
      icon: <Clock className="h-4 w-4 text-amber-600" />,
    });

    impacts.push({
      type: "info",
      title: "Tenure Calculation Updated",
      description: "Employee's years of service and tenure-based benefits will be recalculated.",
      icon: <Briefcase className="h-4 w-4 text-blue-600" />,
    });

    impacts.push({
      type: "info",
      title: "Time-Off Accrual Affected",
      description: "PTO and vacation accrual balances and rates may be adjusted based on the new hire date and tenure tier.",
      icon: <Calendar className="h-4 w-4 text-blue-600" />,
    });

    if (currentEnrollments.length > 0 && !isEarlier) {
      impacts.push({
        type: "warning",
        title: "Review Active Enrollments",
        description: `Employee has ${currentEnrollments.length} active enrollment(s). Verify enrollment effective dates are consistent with new hire date.`,
        icon: <Heart className="h-4 w-4 text-amber-600" />,
      });
    }

    if (!isEarlier && daysDiff >= 90) {
      impacts.push({
        type: "warning",
        title: "Potential Break in Service",
        description: "A hire date change of 90+ days later may constitute a break in service. Consider whether this is a rehire requiring a new waiting period.",
        icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
      });
    }

    if (!isEarlier) {
      impacts.push({
        type: "info",
        title: "ACA Initial Measurement Period",
        description: "For Variable Hour employees, the ACA initial measurement period start date may need to be reset based on the new hire date.",
        icon: <Briefcase className="h-4 w-4 text-blue-600" />,
      });
    }
  }

  return impacts;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function getImpacts(change: EmploymentChangeData): ImpactItem[] {
  // `??` (not `||`) so a real plan value of 0 days is honored — the legacy
  // `|| 60` overrode it with a fabricated 60-day wait whenever the plan had
  // waitingPeriod === 0. Default to 0 (immediate) when no plan context is
  // supplied; callers that want richer impact messaging should pass the
  // plan's real waitingPeriod.
  const waitingPeriod = change.waitingPeriodDays ?? 0;
  const enrollments = change.currentEnrollments || [];

  switch (change.type) {
    case "employmentType":
      return analyzeEmploymentTypeChange(
        change.oldValue as string | null,
        change.newValue as string | null,
        enrollments,
        waitingPeriod
      );
    case "scheduledHours":
      return analyzeScheduledHoursChange(
        change.oldValue as number | null,
        change.newValue as number | null,
        enrollments,
        waitingPeriod
      );
    case "hireDate":
      return analyzeHireDateChange(
        change.oldValue as string | null,
        change.newValue as string | null,
        enrollments,
        waitingPeriod
      );
    default:
      return [];
  }
}

function getChangeTitle(change: EmploymentChangeData): string {
  switch (change.type) {
    case "employmentType":
      return "Employment Type Change";
    case "scheduledHours":
      return "Scheduled Hours Change";
    case "hireDate":
      return "Hire Date Change";
    default:
      return "Employment Change";
  }
}

function getReasonOptions(type: EmploymentChangeType) {
  if (type === "hireDate") {
    return HIRE_DATE_CHANGE_REASONS;
  }
  return EMPLOYMENT_CHANGE_REASONS;
}

interface EmploymentChangeVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: VerificationResult) => void;
  change: EmploymentChangeData | null;
  changes?: EmploymentChangeData[];
}

export function EmploymentChangeVerificationModal({
  isOpen,
  onClose,
  onConfirm,
  change,
  changes = [],
}: EmploymentChangeVerificationModalProps) {
  const [effectiveDate, setEffectiveDate] = React.useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [reason, setReason] = React.useState<string>("");
  const [notes, setNotes] = React.useState<string>("");

  React.useEffect(() => {
    if (isOpen) {
      setEffectiveDate(new Date().toISOString().split("T")[0]);
      setReason("");
      setNotes("");
    }
  }, [isOpen]);

  const allChanges = changes.length > 0 ? changes : (change ? [change] : []);
  
  if (allChanges.length === 0) return null;

  const allImpacts: ImpactItem[] = [];
  allChanges.forEach(c => {
    const impacts = getImpacts(c);
    impacts.forEach(impact => {
      if (!allImpacts.some(existing => existing.title === impact.title)) {
        allImpacts.push(impact);
      }
    });
  });

  const hasLossImpacts = allImpacts.some((i) => i.type === "loss");
  const hasGainImpacts = allImpacts.some((i) => i.type === "gain");
  
  const hasHireDateChange = allChanges.some(c => c.type === "hireDate");
  const reasonOptions = hasHireDateChange ? HIRE_DATE_CHANGE_REASONS : EMPLOYMENT_CHANGE_REASONS;
  
  const employeeName = allChanges[0]?.employeeName;

  const handleConfirm = () => {
    if (!reason) {
      return;
    }
    onConfirm({
      confirmed: true,
      effectiveDate,
      reason,
      notes: notes.trim() || undefined,
    });
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasLossImpacts ? (
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            ) : hasGainImpacts ? (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-blue-500" />
            )}
            {allChanges.length > 1 
              ? "Employment Changes Verification Required"
              : `${getChangeTitle(allChanges[0])} Verification Required`
            }
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {allChanges.map((chg, idx) => (
            <Alert
              key={idx}
              variant={hasLossImpacts ? "destructive" : "default"}
              className={hasLossImpacts ? "" : "border-blue-200 bg-blue-50 dark:bg-blue-950"}
            >
              <AlertDescription>
                {idx === 0 && employeeName && (
                  <span className="font-medium">{employeeName}: </span>
                )}
                {chg.field} changing from{" "}
                <Badge variant="secondary" className="mx-1">
                  {chg.oldValue?.toString() || "Not Set"}
                </Badge>{" "}
                to{" "}
                <Badge variant="default" className="mx-1">
                  {chg.newValue?.toString() || "Not Set"}
                </Badge>
              </AlertDescription>
            </Alert>
          ))}

          <div className="border rounded-lg p-4 bg-muted/30">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Impact Analysis
            </h4>
            <div className="space-y-3">
              {allImpacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No significant impacts detected for {allChanges.length > 1 ? "these changes" : "this change"}.
                </p>
              ) : (
                allImpacts.map((impact, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 p-3 rounded-lg ${
                      impact.type === "gain"
                        ? "bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800"
                        : impact.type === "loss"
                        ? "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800"
                        : impact.type === "warning"
                        ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
                        : "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800"
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">{impact.icon}</div>
                    <div>
                      <p className="font-medium text-sm">{impact.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {impact.description}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="effectiveDate" className="flex items-center gap-1">
                Effective Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="effectiveDate"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">
                The date when this change takes effect for benefits and payroll purposes.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reason" className="flex items-center gap-1">
                Reason for Change <span className="text-red-500">*</span>
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="max-w-sm">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Input
                id="notes"
                placeholder="Enter any additional context or notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {hasLossImpacts && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This change may result in loss of benefits. Please ensure you
                have reviewed all impacts and will complete any required
                notifications (e.g., COBRA notices) as applicable.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!reason || !effectiveDate}
            variant={hasLossImpacts ? "destructive" : "default"}
          >
            Confirm Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function detectEmploymentChanges(
  oldData: any,
  newData: any,
  employeeName?: string,
  currentEnrollments?: any[]
): EmploymentChangeData[] {
  const changes: EmploymentChangeData[] = [];

  // Handle both camelCase and snake_case field names from API
  const oldType = oldData?.employmentType || oldData?.employment_type || null;
  const newType = newData?.employmentType || newData?.employment_type || null;
  // Use case-insensitive comparison for employment type (e.g., "Full-time" vs "full-time")
  const normalizedOldType = oldType?.toLowerCase?.()?.trim?.() || null;
  const normalizedNewType = newType?.toLowerCase?.()?.trim?.() || null;
  if (normalizedOldType !== normalizedNewType && (oldType || newType)) {
    changes.push({
      type: "employmentType",
      field: "Employment Type",
      oldValue: oldType,
      newValue: newType,
      employeeName,
      currentEnrollments,
    });
  }

  // Handle both camelCase and snake_case for scheduled hours
  const oldHoursRaw = oldData?.scheduledHours ?? oldData?.scheduled_hours;
  const newHoursRaw = newData?.scheduledHours ?? newData?.scheduled_hours;
  const oldHours = oldHoursRaw 
    ? (typeof oldHoursRaw === 'string' 
        ? parseFloat(oldHoursRaw) 
        : oldHoursRaw)
    : null;
  const newHours = newHoursRaw 
    ? (typeof newHoursRaw === 'string' 
        ? parseFloat(newHoursRaw) 
        : newHoursRaw)
    : null;
  
  if (oldHours !== newHours && (oldHours !== null || newHours !== null)) {
    changes.push({
      type: "scheduledHours",
      field: "Scheduled Hours/Week",
      oldValue: oldHours,
      newValue: newHours,
      employeeName,
      currentEnrollments,
    });
  }

  // Handle both camelCase and snake_case for hire date
  const oldHireDateRaw = oldData?.hireDate ?? oldData?.hire_date;
  const newHireDateRaw = newData?.hireDate ?? newData?.hire_date;
  const oldHireDate = oldHireDateRaw 
    ? (typeof oldHireDateRaw === 'string' 
        ? oldHireDateRaw.split('T')[0] 
        : new Date(oldHireDateRaw).toISOString().split('T')[0])
    : null;
  const newHireDate = newHireDateRaw 
    ? (typeof newHireDateRaw === 'string' 
        ? newHireDateRaw.split('T')[0] 
        : new Date(newHireDateRaw).toISOString().split('T')[0])
    : null;
  
  if (oldHireDate !== newHireDate && (oldHireDate || newHireDate)) {
    changes.push({
      type: "hireDate",
      field: "Hire Date",
      oldValue: oldHireDate,
      newValue: newHireDate,
      employeeName,
      currentEnrollments,
    });
  }

  return changes;
}

export function useEmploymentChangeVerification() {
  const [pendingChanges, setPendingChanges] = React.useState<EmploymentChangeData[]>([]);
  const resolveRef = React.useRef<((results: VerificationResult[] | null) => void) | null>(null);

  const verifyChanges = React.useCallback(
    (changes: EmploymentChangeData[]): Promise<VerificationResult[] | null> => {
      if (changes.length === 0) {
        return Promise.resolve([]);
      }

      return new Promise((resolve) => {
        resolveRef.current = resolve;
        setPendingChanges(changes);
      });
    },
    []
  );

  const handleConfirm = React.useCallback(
    (result: VerificationResult) => {
      const results = pendingChanges.map(() => result);
      setPendingChanges([]);
      if (resolveRef.current) {
        resolveRef.current(results);
        resolveRef.current = null;
      }
    },
    [pendingChanges]
  );

  const handleClose = React.useCallback(() => {
    setPendingChanges([]);
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, []);

  const VerificationModal = React.useCallback(
    () => (
      <EmploymentChangeVerificationModal
        isOpen={pendingChanges.length > 0}
        onClose={handleClose}
        onConfirm={handleConfirm}
        change={null}
        changes={pendingChanges}
      />
    ),
    [pendingChanges, handleClose, handleConfirm]
  );

  return {
    verifyChanges,
    VerificationModal,
    isVerifying: pendingChanges.length > 0,
  };
}
